/**
 * useCalculateEngagement — Phase 6a.11 per Spec §21.7.9.
 *
 * Owns the Calculate flow lifecycle for a single engagement:
 *
 *   1. Load the engagement from localStorage.
 *   2. POST it to `/api/engagements/calculate` via `engagementService`
 *      (which already injects `functionalCurrency` per Finding Q).
 *   3. On 200 → persist `lastResult`, run `transitionToCalculated`,
 *      clear any prior `lastCalculateError`, navigate to `/results`.
 *   4. On 400 validation error → parse the per-contract `failedContracts`
 *      into `lastCalculateError: Record<number, string[]>`, persist,
 *      navigate back to the contract list (rejection banner + per-slot
 *      `✗` indicators light up via the auto-clear-on-edit mechanism).
 *   5. On `AbortError` → silent: dismiss overlay, leave state.
 *   6. On generic network/server error → bubble up `lastNetworkError`
 *      so the caller can show a toast/admonition with retry.
 *
 * The hook exposes:
 *   • `calculate()`        — fires the flow
 *   • `cancel()`           — calls `AbortController.abort()`
 *   • `isCalculating`      — drives the loading overlay
 *   • `lastNetworkError`   — display string for transient failures
 *   • `clearError()`       — caller dismisses the error
 */

import * as React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { ApiError } from "@/api";
import { useEngagementService, useHistoryStorage } from "@/services";
import type {
  CalculateEngagementRequest,
  CalculateEngagementResponse,
  ContractInput,
  EngagementHistoryEntry,
  EngagementInput,
} from "@/types";

import { transitionToCalculated } from "./engagement-state";

interface UseCalculateEngagementReturn {
  calculate: () => Promise<void>;
  cancel: () => void;
  isCalculating: boolean;
  lastNetworkError: string | null;
  clearError: () => void;
}

// ─── SPEC-10 / Finding #14 — structured-400 routing helpers ───────────────
interface FailedContractDetail {
  contractIndex: number;
  contractId: string;
  errors: Array<{ field: string; code: string }>;
}
function extractFailedContracts(details: unknown): FailedContractDetail[] {
  if (
    details &&
    typeof details === "object" &&
    Array.isArray((details as { failedContracts?: unknown }).failedContracts)
  ) {
    return (details as { failedContracts: FailedContractDetail[] }).failedContracts;
  }
  return [];
}
function humanizeRejection(field: string, code: string): string {
  if (field === "(calculator)") {
    const sep = code.indexOf(" / ");
    return (sep >= 0 ? code.slice(sep + 3) : code).trim();
  }
  return `${field}: ${code}`;
}

export function useCalculateEngagement(
  engagementId: string,
): UseCalculateEngagementReturn {
  const historyStorage = useHistoryStorage();
  const engagementService = useEngagementService();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [isCalculating, setIsCalculating] = React.useState(false);
  const [lastNetworkError, setLastNetworkError] = React.useState<string | null>(
    null,
  );
  const abortRef = React.useRef<AbortController | null>(null);

  const clearError = React.useCallback(() => setLastNetworkError(null), []);

  const cancel = React.useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const calculate = React.useCallback(async () => {
    setLastNetworkError(null);
    const engagement = await historyStorage.getEngagement(engagementId);
    if (!engagement) return;

    // Build the request payload from engagement state. Two filters
    // compose here:
    //   1) Skip `null` slots defensively — Spec §21.7.9 invariant: the
    //      Calculate button is disabled when any slot is non-`filled`.
    //   2) Phase 6a.22a — skip soft-deleted slots. The backend never
    //      sees deleted contracts; the slot indices the backend echoes
    //      back refer to this dense filtered list, NOT the original
    //      `engagement.contracts` array. The visible list in
    //      ContractListPage is built from the same predicate so both
    //      stay in lock-step.
    // Phase 6e.4.2-hotfix-2 — soft-delete is gone (Bug D). Just filter
    // out null slots; the contracts array is a true list of live slots.
    const contracts = engagement.contracts.filter(
      (c): c is ContractInput => c !== null,
    );
    // Phase 6c.2 (Frontend Spec §21.6.2 / §21.6.3, Backend §20.3) —
    // forward the auditor-chosen `openingBalanceMode` + MANUAL
    // `openingBalances` captured at engagement creation. Legacy
    // entries persisted before the mode was tracked default to
    // `IFRS_FIRST_ADOPTION` (matches the legacy `"AUTO"` semantic).
    const engagementInput: EngagementInput = {
      clientName: engagement.clientName,
      auditPeriodStart: engagement.auditPeriodStart,
      auditPeriodEnd: engagement.auditPeriodEnd,
      functionalCurrency: engagement.functionalCurrency,
      openingBalanceMode: engagement.openingBalanceMode ?? "IFRS_FIRST_ADOPTION",
      ...(engagement.openingBalances
        ? { openingBalances: engagement.openingBalances }
        : {}),
      // Phase 6a.23 — closing FX rates flow through verbatim. Backend
      // Spec §20.3 line 2230 rejects with `MISSING_CLOSING_FX_RATE`
      // when a contract uses a foreign currency that this map does
      // not cover; the engagement-form section is the single source
      // of these values.
      ...(engagement.closingFxRates &&
      Object.keys(engagement.closingFxRates).length > 0
        ? { closingFxRates: engagement.closingFxRates }
        : {}),
    };
    // Phase 6e.4.6 (Bug F1) — propagate the engagement's audit window
    // onto each contract's `engagementPeriodEnd` / `engagementPeriodStart`.
    // Backend uses `engagementPeriodEnd` as the reporting date for the
    // current/non-current liability split (IAS 1.69 + IFRS 16 §47(b));
    // when this field is undefined, the calculator now returns
    // `liabilitySplit = undefined` (defensive). Before this fix the
    // calculator fell back to the lease-end row, producing a zero-
    // everywhere split that left every balance sheet's lease-liability
    // line at zero for every active lease — a P0 audit-grade defect.
    const contractsWithEngagementWindow = contracts.map((c) => ({
      ...c,
      engagementPeriodStart: engagement.auditPeriodStart,
      engagementPeriodEnd: engagement.auditPeriodEnd,
    }));
    const payload: CalculateEngagementRequest = {
      engagement: engagementInput,
      // The engagement-layer endpoint expects `assetClass` on every
      // contract (Backend §20.3). The contract form's Zod schema
      // enforces this; the cast here is safe because all slot-filled
      // contracts have already passed validation.
      contracts: contractsWithEngagementWindow as CalculateEngagementRequest["contracts"],
    };

    const controller = new AbortController();
    abortRef.current = controller;
    setIsCalculating(true);

    try {
      const response: CalculateEngagementResponse =
        await engagementService.calculate(payload, {
          signal: controller.signal,
        });

      if (response.success) {
        // ── Happy path ────────────────────────────────────────────────
        // Phase 6c.2 — persist `warnings` alongside the result so the
        // WarningsBanner re-renders on every result page open without
        // forcing a recalculation.
        let updated: EngagementHistoryEntry = {
          ...engagement,
          lastResult: {
            engagementId: response.data.engagementId,
            perContract: response.data.perContract,
            aggregations: response.data.aggregations,
            calculatedAt: new Date().toISOString(),
            ...(response.data.warnings && response.data.warnings.length > 0
              ? { warnings: response.data.warnings }
              : {}),
          },
          lastCalculateError: undefined,
          updatedAt: new Date().toISOString(),
        };
        updated = transitionToCalculated(updated);
        await historyStorage.saveEngagement(updated);
        navigate(`/engagements/${engagementId}/results`);
        return;
      }
    } catch (err) {
      // AbortError — silent close.
      if (
        controller.signal.aborted ||
        (err instanceof DOMException && err.name === "AbortError") ||
        (err instanceof Error && err.name === "AbortError")
      ) {
        return;
      }
      // SPEC-10 / Finding #14 — structured 400 with per-contract failures
      // routes through the rejection-banner path. The fail-fast route
      // throws on 400 (no success:false body reaches here), so this is
      // the only place the rejection map is populated.
      if (err instanceof ApiError && err.statusCode === 400) {
        const failed = extractFailedContracts(err.details);
        if (failed.length > 0) {
          const nextErrors: Record<number, string[]> = {};
          for (const fc of failed) {
            nextErrors[fc.contractIndex - 1] = fc.errors.map((e) =>
              humanizeRejection(e.field, e.code),
            );
          }
          await historyStorage.saveEngagement({
            ...engagement,
            lastCalculateError:
              Object.keys(nextErrors).length > 0 ? nextErrors : undefined,
            updatedAt: new Date().toISOString(),
          });
          navigate(`/engagements/${engagementId}/contracts`);
          return;
        }
      }
      // SPEC-TIER Part D — a 403 TIER_LIMIT_EXCEEDED is surfaced as an
      // actionable toast with an "upgrade plan" link to /settings. The inline
      // admonition channel (lastNetworkError) is string-only and can't carry a
      // router Link, so we handle it here where the ApiError code is in scope.
      if (err instanceof ApiError && err.code === "TIER_LIMIT_EXCEEDED") {
        toast.error(t("tierLimit.body"), {
          action: {
            label: t("tierLimit.upgradeCta"),
            onClick: () => navigate("/settings"),
          },
        });
        return;
      }
      // Network / cold-start / 5xx — surface to caller for toast/admonition.
      if (err instanceof ApiError) {
        setLastNetworkError(
          err.messageTr ?? err.message ?? t("engagement.calculate.networkError"),
        );
      } else {
        setLastNetworkError(t("engagement.calculate.networkError"));
      }
    } finally {
      abortRef.current = null;
      setIsCalculating(false);
    }
  }, [engagementId, engagementService, historyStorage, navigate, t]);

  return {
    calculate,
    cancel,
    isCalculating,
    lastNetworkError,
    clearError,
  };
}
